import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserPreferences, updateUserPreferences, resetUserPreferences, UserPreferences } from '../lib/preferences.api';
import { useNotificationStore } from '../stores/notification.store';

export default function Preferences() {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    emailNotifications: true,
    pushNotifications: true,
    dailyReportTime: '08:00',
    timezone: 'UTC',
    theme: 'dark',
    language: 'en',
    dataSharing: false,
    analyticsEnabled: true,
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setIsLoading(true);
      const prefs = await getUserPreferences();
      setPreferences(prefs);
      setFormData({
        emailNotifications: prefs.emailNotifications,
        pushNotifications: prefs.pushNotifications,
        dailyReportTime: prefs.dailyReportTime,
        timezone: prefs.timezone,
        theme: prefs.theme,
        language: prefs.language,
        dataSharing: prefs.dataSharing,
        analyticsEnabled: prefs.analyticsEnabled,
      });
    } catch (error) {
      console.error('Error loading preferences:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to load preferences',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = await updateUserPreferences(formData);
      setPreferences(updated);

      addNotification({
        type: 'success',
        title: 'Preferences Saved',
        message: 'Your preferences have been updated successfully',
      });
    } catch (error: any) {
      console.error('Error saving preferences:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error || 'Failed to save preferences',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset all preferences to default values?')) {
      return;
    }

    try {
      setIsSaving(true);
      const reset = await resetUserPreferences();
      setPreferences(reset);
      setFormData({
        emailNotifications: reset.emailNotifications,
        pushNotifications: reset.pushNotifications,
        dailyReportTime: reset.dailyReportTime,
        timezone: reset.timezone,
        theme: reset.theme,
        language: reset.language,
        dataSharing: reset.dataSharing,
        analyticsEnabled: reset.analyticsEnabled,
      });

      addNotification({
        type: 'success',
        title: 'Preferences Reset',
        message: 'All preferences have been reset to default values',
      });
    } catch (error) {
      console.error('Error resetting preferences:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to reset preferences',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const timezones = [
    'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
    'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Singapore', 'Australia/Sydney'
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'pt', name: 'Português' },
    { code: 'ja', name: '日本語' },
    { code: 'zh', name: '中文' },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-600">
            Loading preferences...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-slate-600 hover:text-slate-900 mb-4 flex items-center transition-colors group"
          >
            <svg className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Preferences
            </h1>
            <p className="text-slate-600 text-sm">
              Customize your Mundus Novus experience
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Notification Settings */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <span>🔔</span>
            <span>Notification Settings</span>
          </h2>

          <div className="space-y-4">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-1">
                <label className="block text-slate-900 font-semibold mb-1">
                  Email Notifications
                </label>
                <p className="text-sm text-slate-600">
                  Receive daily reports and important updates via email
                </p>
              </div>
              <button
                onClick={() => handleChange('emailNotifications', !formData.emailNotifications)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  formData.emailNotifications ? 'bg-green-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    formData.emailNotifications ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-1">
                <label className="block text-slate-900 font-semibold mb-1">
                  Push Notifications
                </label>
                <p className="text-sm text-slate-600">
                  Get real-time updates about your Mundus achievements and reports
                </p>
              </div>
              <button
                onClick={() => handleChange('pushNotifications', !formData.pushNotifications)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  formData.pushNotifications ? 'bg-green-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    formData.pushNotifications ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Daily Report Time */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <label className="block text-slate-900 font-semibold mb-2">
                Daily Report Time
              </label>
              <p className="text-sm text-slate-600 mb-3">
                When would you like to receive your daily reports?
              </p>
              <input
                type="time"
                value={formData.dailyReportTime}
                onChange={(e) => handleChange('dailyReportTime', e.target.value)}
                className="w-full md:w-64 bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Timezone */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <label className="block text-slate-900 font-semibold mb-2">
                Timezone
              </label>
              <p className="text-sm text-slate-600 mb-3">
                Your local timezone for scheduling and reports
              </p>
              <select
                value={formData.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <span>🎨</span>
            <span>Display Settings</span>
          </h2>

          <div className="space-y-4">
            {/* Language */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <label className="block text-slate-900 font-semibold mb-2">
                Language
              </label>
              <p className="text-sm text-slate-600 mb-3">
                Interface language
              </p>
              <select
                value={formData.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <span>🔒</span>
            <span>Privacy Settings</span>
          </h2>

          <div className="space-y-4">
            {/* Data Sharing */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-1">
                <label className="block text-slate-900 font-semibold mb-1">
                  Data Sharing
                </label>
                <p className="text-sm text-slate-600">
                  Share anonymized usage data to help improve Mundus Novus
                </p>
              </div>
              <button
                onClick={() => handleChange('dataSharing', !formData.dataSharing)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  formData.dataSharing ? 'bg-green-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    formData.dataSharing ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-1">
                <label className="block text-slate-900 font-semibold mb-1">
                  Analytics
                </label>
                <p className="text-sm text-slate-600">
                  Enable analytics to track your progress and generate insights
                </p>
              </div>
              <button
                onClick={() => handleChange('analyticsEnabled', !formData.analyticsEnabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  formData.analyticsEnabled ? 'bg-green-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    formData.analyticsEnabled ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <>💾 Save Preferences</>
            )}
          </button>

          <button
            onClick={handleReset}
            disabled={isSaving}
            className="sm:w-auto border-2 border-red-600 text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            🔄 Reset to Default
          </button>
        </div>

        {/* Last Updated */}
        {preferences && (
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">
              Last updated: {new Date(preferences.updatedAt).toLocaleString()}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
